export type SessionEndReason = 'visibilitychange' | 'pagehide' | 'blur' | 'beforeunload' | 'unload';

export type SessionRestartReason = 'visibilitychange' | 'pageshow' | 'focus';

const SESSION_END_WIN_EVENTS: Array<keyof WindowEventMap> = ['pagehide', 'beforeunload', 'unload'];

const SESSION_RESTART_WIN_EVENTS: Array<keyof WindowEventMap> = ['pageshow', 'focus'];

export interface BrowserLifecycleHandlers {
	onSessionEnd: (reason: SessionEndReason) => void;
	onSessionRestart: (reason: SessionRestartReason) => void;
}

// Wire up the page-lifecycle DOM events (visibilitychange, pagehide,
// beforeunload, unload, blur, pageshow, focus) and route each one to the
// supplied `onSessionEnd` / `onSessionRestart` callback with a reason tag.
export class BrowserLifecycleListeners {
	private started = false;
	private teardown: Array<() => void> = [];
	private readonly handlers: BrowserLifecycleHandlers;

	public constructor(handlers: BrowserLifecycleHandlers) {
		this.handlers = handlers;
	}

	public start = () => {
		if (this.started) {
			return;
		}
		if (typeof document === 'undefined' || typeof window === 'undefined') {
			this.started = true;
			return;
		}
		this.started = true;

		// Single visibilitychange listener that dispatches to the appropriate
		// callback based on the current visibilityState.
		const handleVisibility = () => {
			if (document.visibilityState === 'hidden') {
				this.handlers.onSessionEnd('visibilitychange');
			} else if (document.visibilityState === 'visible') {
				this.handlers.onSessionRestart('visibilitychange');
			}
		};
		document.addEventListener('visibilitychange', handleVisibility);
		this.teardown.push(() => document.removeEventListener('visibilitychange', handleVisibility));

		SESSION_END_WIN_EVENTS.forEach((evt) => {
			const handler = () => this.handlers.onSessionEnd(evt as SessionEndReason);
			window.addEventListener(evt, handler);
			this.teardown.push(() => window.removeEventListener(evt, handler));
		});

		// `blur` fires for many non-session-ending reasons (iframe focus, native
		// dropdowns, alert/print dialogs). Defer one microtask and confirm the
		// window genuinely lost focus before treating it as a session end.
		const handleBlur = (event: FocusEvent) => {
			if (event.target !== window) {
				return;
			}
			queueMicrotask(() => {
				if (!document.hasFocus()) {
					this.handlers.onSessionEnd('blur');
				}
			});
		};
		window.addEventListener('blur', handleBlur);
		this.teardown.push(() => window.removeEventListener('blur', handleBlur));

		SESSION_RESTART_WIN_EVENTS.forEach((evt) => {
			const handler = () => this.handlers.onSessionRestart(evt as SessionRestartReason);
			window.addEventListener(evt, handler);
			this.teardown.push(() => window.removeEventListener(evt, handler));
		});
	};

	public stop = () => {
		if (!this.started) {
			return;
		}
		this.started = false;
		this.teardown.forEach((off) => off());
		this.teardown = [];
	};
}
